/**
 * @module beebee-base/lib/utility/ajax
 * @description AJAX request helpers. jQuery-free: POST/PATCH/DELETE go through
 * native `fetch`, and object payloads are serialized with `bbParam` — a faithful
 * port of jQuery 3.7.1 `jQuery.param` (traditional=false). This preserves the
 * exact `application/x-www-form-urlencoded` wire format the API expects
 * (deep-bracket nesting `a[b][]=1`, `encodeURIComponent` + `%20`→`+`), which
 * `URLSearchParams` does NOT reproduce (it stringifies nested objects to
 * "[object Object]" and encodes `' ( ) ! ~ *` differently).
 */


/**
 * Faithful port of jQuery 3.7.1 `jQuery.param(obj)` with `traditional=false`.
 * Byte-identical to the previous jQuery serialization: arrays → `k[]=v`, nested
 * objects → `k[sub]=v`, arrays of objects → `k[0][sub]=v`, null/undefined → `k=`,
 * value coercion via `String()` (NO Date special-casing — matches jQuery), then
 * `encodeURIComponent` with `%20` replaced by `+`.
 * @param {Object} a - payload object
 * @returns {string} url-encoded body
 */
function bbParam(a) {
  const rbracket = /\[\]$/;
  const s = [];
  const add = (key, valueOrFn) => {
    const value = typeof valueOrFn === 'function' ? valueOrFn() : valueOrFn;
    // Drop keys whose value is `undefined` (accidental/absent frontend data) so we
    // never emit an empty `key=` — that empty param made backends reject the whole
    // request with 400 "Resource Not Found" (e.g. FSE save, calendar /events,
    // maestro refreshAllEvents, ecommerce dashboard). Explicit `null` and '' are
    // KEPT as `key=` so POST/PATCH field-clears still work.
    if (value === undefined) {
      return;
    }
    s.push(encodeURIComponent(key) + '=' + encodeURIComponent(value === null ? '' : value));
  };
  const buildParams = (prefix, obj) => {
    if (Array.isArray(obj)) {
      obj.forEach((v, i) => {
        if (rbracket.test(prefix)) {
          add(prefix, v);
        } else {
          buildParams(prefix + '[' + (v !== null && typeof v === 'object' ? i : '') + ']', v);
        }
      });
    } else if (obj !== null && typeof obj === 'object') {
      for (const name in obj) {
        buildParams(prefix + '[' + name + ']', obj[name]);
      }
    } else {
      add(prefix, obj);
    }
  };
  for (const prefix in a) {
    buildParams(prefix, a[prefix]);
  }
  return s.join('&').replace(/%20/g, '+');
}

/**
 * Global in-flight request counter driving the `body.beebee-loading` class.
 * Replaces the old jQuery `ajaxStart`/`ajaxStop` hooks (core-app.js), which no
 * longer fire now that every request uses native fetch instead of jQuery.ajax.
 * @private
 */
let _inFlight = 0;
function _setLoading(active) {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  if (active) {
    _inFlight += 1;
    document.body.classList.add('beebee-loading');
  } else {
    _inFlight = Math.max(0, _inFlight - 1);
    if (_inFlight === 0) {
      document.body.classList.remove('beebee-loading');
    }
  }
}

/**
 * Crea le opzioni di autenticazione per le richieste AJAX
 * @private
 * @param {string|null} token - Token di autenticazione
 * @returns {Object} Headers con autorizzazione
 */
function _createAuthHeaders(token) {
  return {
    Authorization: token ? `Bearer ${token}` : ''
  };
}

/**
 * Core fetch wrapper preserving the previous jQuery.ajax semantics:
 * - `dataType:'json'` → response parsed as JSON (empty body → null).
 * - 401 → warn + `window.location.reload()` + reject.
 * - other non-2xx → Error('[beebeeAjax] Request failed: <status> <statusText>')
 *   with `err.responseJSON` populated from the parsed body when available.
 * @private
 */
async function _fetchRequest({
  method,
  url,
  token,
  data,
  json = false
}) {
  const headers = _createAuthHeaders(token);
  const init = {
    method,
    headers,
    // withCredentials:false → same-origin credential behavior (jQuery XHR default).
    credentials: 'same-origin',
    cache: 'no-store'
  };

  // POST/PATCH with an object payload: replicate jQuery's default url-encoding,
  // unless `json` is set (explicit application/json sites → JSON.stringify).
  if (data !== null && data !== undefined) {
    if (json) {
      headers['Content-Type'] = 'application/json; charset=UTF-8';
      init.body = typeof data === 'string' ? data : JSON.stringify(data);
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
      init.body = typeof data === 'string' ? data : bbParam(data);
    }
  }
  _setLoading(true);
  try {
    let response;
    try {
      response = await fetch(url, init);
    } catch (networkErr) {
      const err = new Error(`[beebeeAjax] Request failed: 0 ${networkErr.message || 'network error'}`);
      err.status = 0;
      err.responseJSON = undefined;
      throw err;
    }

    // Parse body as JSON (dataType:'json'); tolerate empty/non-JSON bodies.
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (response.ok) {
      return parsed;
    }
    if (response.status === 401) {
      console.warn('[beebeeAjax] 401 Unauthorized — reloading session');
      window.location.reload();
      throw new Error('Unauthorized (401)');
    }
    const err = new Error(`[beebeeAjax] Request failed: ${response.status} ${response.statusText || 'unknown'}`);
    err.status = response.status;
    err.responseJSON = parsed ?? undefined;
    throw err;
  } finally {
    _setLoading(false);
  }
}

/**
 * Esegue una richiesta DELETE
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxDelete(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'DELETE',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta PATCH
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPatch(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'PATCH',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta POST
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPost(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'POST',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta POST con body JSON (application/json).
 * Sostituisce i `jQuery.ajax({ contentType: 'application/json', data: JSON.stringify(...) })`.
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare (serializzati con JSON.stringify)
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPostJson(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'POST',
    url,
    token,
    data,
    json: true
  });
}

/**
 * Esegue una richiesta GET con fetch API
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object} data - Query parameters
 * @returns {Promise<Object>} Risposta JSON o oggetto errore
 */
async function beebeeAjaxGet(url, token = null, data = {}) {
  if (!url) {
    return {
      success: false,
      message: 'Missing URL'
    };
  }
  const completeURL = new URL(url.startsWith('http') ? url : url, window.location.origin);
  const filteredData = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== null && value !== undefined));
  for (const [key, value] of Object.entries(filteredData)) {
    if (Array.isArray(value)) {
      value.forEach(val => completeURL.searchParams.append(`${key}[]`, val));
    } else {
      completeURL.searchParams.append(key, value);
    }
  }
  completeURL.searchParams.append('_', Date.now());
  _setLoading(true);
  try {
    // GET now REJECTS on error, matching beebeeAjaxPost/Patch/Delete (and the old
    // jQuery.ajax contract). The previous version resolved `{ success:false }` on any
    // failure, which silently killed every caller's `.catch()`/findError branch — a
    // fleet-wide regression from the jQuery removal (see
    // docs/test-analysis/WORKSPACE-COVERAGE-MASTER-PLAN.md §7ter). Callers that guarded only the old
    // resolve-with-{success:false} shape need an explicit try/catch alongside that
    // guard: the guard still covers a 200 whose body encodes a logical failure, but
    // network/HTTP errors now reject and escape uncaught without one.
    // Keep this a CORS "simple request": no custom headers, no credentials mode
    // that forces credentials. `Cache-Control`/`Pragma`/`Expires` and an always-on
    // `Authorization` header are non-simple → they trigger a preflight (OPTIONS)
    // that the cross-origin asset PHP server does not answer, surfacing as a CORS
    // error. Cache busting is already handled by the `_=Date.now()` query param
    // appended above (matches the old jQuery `cache:false` + `withCredentials:false`
    // behavior). Only send `Authorization` when a token is actually present.
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let response;
    try {
      response = await fetch(completeURL, {
        method: 'GET',
        headers,
        cache: 'no-store',
        credentials: 'same-origin'
      });
    } catch (networkErr) {
      const err = new Error(`[beebeeAjax] GET failed: 0 ${networkErr.message || 'network error'}`);
      err.status = 0;
      err.responseJSON = undefined;
      throw err;
    }
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (response.ok) {
      return parsed;
    }
    if (response.status === 401) {
      console.warn('[beebeeAjax] 401 Unauthorized — reloading session');
      window.location.reload();
      throw new Error('Unauthorized (401)');
    }
    const err = new Error(`[beebeeAjax] GET failed: ${response.status} ${response.statusText || 'unknown'}`);
    err.status = response.status;
    err.responseJSON = parsed ?? undefined;
    throw err;
  } finally {
    _setLoading(false);
  }
}

export { bbParam, beebeeAjaxDelete, beebeeAjaxGet, beebeeAjaxPatch, beebeeAjaxPost, beebeeAjaxPostJson };
//# sourceMappingURL=ajax.js.map
